/**
 * E-Invoice Module Enums
 * DDV E-Invoice Module (FAST Business Online)
 *
 * Canonical state machine + reason codes + FAST method mapping.
 */

// ─── Sale Types ────────────────────────────────────────────
export const EINVOICE_SALE_TYPE = Object.freeze({
  RETAIL: 'RETAIL',
  WHOLESALE: 'WHOLESALE',
});

// ─── Provider Modes ────────────────────────────────────────
export const EINVOICE_PROVIDER_MODE = Object.freeze({
  FAST_POS: 'FAST_POS', // Mục 1 – REST tcservice.fast.com.vn
  FAST_PORTAL: 'FAST_PORTAL', // Mục 2 – SOAP PortalService
});

// ─── Sign Modes (Wholesale only) ───────────────────────────
export const EINVOICE_SIGN_MODE = Object.freeze({
  HSM: 'HSM',
  USB: 'USB',
});

// ─── Invoice Lifecycle States (canonical) ──────────────────
export const EINVOICE_STATUS = Object.freeze({
  DRAFT: 1,
  READY_TO_ISSUE: 2,
  ISSUING: 3,
  ISSUED: 4,
  TAX_PENDING: 5,
  TAX_ACCEPTED: 6,
  TAX_REJECTED: 7,
  CANCELED: 8,
  ADJUSTED: 9,
  REPLACED: 10,
  ISSUE_FAILED: 11,
});

export const EINVOICE_STATUS_NAME = Object.freeze({
  [EINVOICE_STATUS.DRAFT]: 'Nháp',
  [EINVOICE_STATUS.READY_TO_ISSUE]: 'Sẵn sàng phát hành',
  [EINVOICE_STATUS.ISSUING]: 'Đang phát hành',
  [EINVOICE_STATUS.ISSUED]: 'Đã phát hành',
  [EINVOICE_STATUS.TAX_PENDING]: 'Chờ CQT',
  [EINVOICE_STATUS.TAX_ACCEPTED]: 'CQT chấp nhận',
  [EINVOICE_STATUS.TAX_REJECTED]: 'CQT từ chối',
  [EINVOICE_STATUS.CANCELED]: 'Đã huỷ',
  [EINVOICE_STATUS.ADJUSTED]: 'Đã điều chỉnh',
  [EINVOICE_STATUS.REPLACED]: 'Đã thay thế',
  [EINVOICE_STATUS.ISSUE_FAILED]: 'Phát hành lỗi',
});

// Bootstrap variant mapping for status badges
export const EINVOICE_STATUS_VARIANT = Object.freeze({
  [EINVOICE_STATUS.DRAFT]: 'secondary',
  [EINVOICE_STATUS.READY_TO_ISSUE]: 'info',
  [EINVOICE_STATUS.ISSUING]: 'warning',
  [EINVOICE_STATUS.ISSUED]: 'primary',
  [EINVOICE_STATUS.TAX_PENDING]: 'warning',
  [EINVOICE_STATUS.TAX_ACCEPTED]: 'success',
  [EINVOICE_STATUS.TAX_REJECTED]: 'danger',
  [EINVOICE_STATUS.CANCELED]: 'dark',
  [EINVOICE_STATUS.ADJUSTED]: 'light',
  [EINVOICE_STATUS.REPLACED]: 'light',
  [EINVOICE_STATUS.ISSUE_FAILED]: 'danger',
});

// Icon mapping for status (FontAwesome)
export const EINVOICE_STATUS_ICON = Object.freeze({
  [EINVOICE_STATUS.DRAFT]: 'fas fa-file',
  [EINVOICE_STATUS.READY_TO_ISSUE]: 'fas fa-file-alt',
  [EINVOICE_STATUS.ISSUING]: 'fas fa-spinner fa-spin',
  [EINVOICE_STATUS.ISSUED]: 'fas fa-file-invoice',
  [EINVOICE_STATUS.TAX_PENDING]: 'fas fa-clock',
  [EINVOICE_STATUS.TAX_ACCEPTED]: 'fas fa-check-circle',
  [EINVOICE_STATUS.TAX_REJECTED]: 'fas fa-times-circle',
  [EINVOICE_STATUS.CANCELED]: 'fas fa-ban',
  [EINVOICE_STATUS.ADJUSTED]: 'fas fa-edit',
  [EINVOICE_STATUS.REPLACED]: 'fas fa-exchange-alt',
  [EINVOICE_STATUS.ISSUE_FAILED]: 'fas fa-exclamation-triangle',
});

// ─── Invoice Relation Types ────────────────────────────────
export const EINVOICE_RELATION_TYPE = Object.freeze({
  ADJUST: 'ADJUST',
  REPLACE: 'REPLACE',
  CANCEL: 'CANCEL',
});

// ─── Adjustment Types (FAST) ───────────────────────────────
export const EINVOICE_ADJUSTMENT_TYPE = Object.freeze({
  INCREASE: 1, // Điều chỉnh tăng
  DECREASE: 2, // Điều chỉnh giảm
  INFO_ONLY: 3, // Điều chỉnh thông tin
  REPLACE: 4, // Thay thế (used internally)
});

export const EINVOICE_ADJUSTMENT_TYPE_NAME = Object.freeze({
  [EINVOICE_ADJUSTMENT_TYPE.INCREASE]: 'Điều chỉnh tăng',
  [EINVOICE_ADJUSTMENT_TYPE.DECREASE]: 'Điều chỉnh giảm',
  [EINVOICE_ADJUSTMENT_TYPE.INFO_ONLY]: 'Điều chỉnh thông tin',
  [EINVOICE_ADJUSTMENT_TYPE.REPLACE]: 'Thay thế',
});

// ─── FAST Method Codes ─────────────────────────────────────
export const FAST_METHOD = Object.freeze({
  // Retail POS (Mục 1)
  POS_ISSUE: 7312,
  POS_STATUS: 7370,
  POS_PDF: 7380,
  POS_CANCEL: 7330,
  POS_ADJUST: 7322,
  POS_REPLACE: 7352,
  POS_PREVIEW: 7312, // action=600
  POS_DRAFT_PUSH: 7311,
  POS_DRAFT_PDF: 7381,
  POS_DRAFT_QUERY: 7371,

  // Wholesale (Mục 2)
  WS_ISSUE: 310,
  WS_ADJUST: 320,
  WS_REPLACE: 350,
  WS_CANCEL: 330,
  WS_QUERY: 370,
  WS_PDF: 380,
  WS_PDF_CONVERT: 385,
  WS_EMAIL: 700,
  WS_TAX_STATUS: 8200,

  // PXK (optional)
  PXK_ISSUE: 319,
  PXK_ADJUST: 328,
  PXK_REPLACE: 358,
  PXK_CANCEL: 338,
  PXK_PDF: 381,
  PXK_PDF_CONVERT: 386,
});

// ─── Tax Status mapping from FAST ──────────────────────────
export const FAST_TAX_STATUS = Object.freeze({
  // Retail POS
  POS_PENDING: 0,
  POS_ACCEPTED: 3,
  POS_REJECTED: 5,

  // Wholesale
  WS_ACCEPTED: 3,
  WS_REJECTED: 4,
});

// ─── FAST Process Types (POS) ──────────────────────────────
export const FAST_PROCESS_TYPE = Object.freeze({
  NORMAL: 1, // Bình thường
  REDUCED_TAX: 2, // Giảm thuế theo NĐ
  NO_TAX: 3, // Không chịu thuế
  NOT_DECLARE: 4, // Không kê khai
});

// ─── Valid Tax Rates ───────────────────────────────────────
export const FAST_TAX_RATES = Object.freeze([0, 5, 8, 10, -1, -2, -9]);

// ─── Reason Codes (normalized FAST error → internal) ───────
export const EINVOICE_REASON_CODE = Object.freeze({
  AUTH_INVALID: 'AUTH_INVALID',
  DUPLICATE_KEY: 'DUPLICATE_KEY',
  DUPLICATE_INVOICE_NO: 'DUPLICATE_INVOICE_NO',
  INVALID_TAXCODE: 'INVALID_TAXCODE',
  INVALID_NEWLINE_CHAR: 'INVALID_NEWLINE_CHAR',
  INVOICE_DATE_RULE: 'INVOICE_DATE_RULE',
  SIGNATURE_INVALID: 'SIGNATURE_INVALID',
  TOO_MANY_LINES: 'TOO_MANY_LINES',
  TAX_REJECTED: 'TAX_REJECTED',
  AMOUNT_WORDS_MISMATCH: 'AMOUNT_WORDS_MISMATCH',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  RATE_LIMIT: 'RATE_LIMIT',
  UNKNOWN: 'UNKNOWN',
});

export const EINVOICE_REASON_CODE_NAME = Object.freeze({
  [EINVOICE_REASON_CODE.AUTH_INVALID]: 'Lỗi xác thực FAST',
  [EINVOICE_REASON_CODE.DUPLICATE_KEY]: 'Trùng key phát hành',
  [EINVOICE_REASON_CODE.DUPLICATE_INVOICE_NO]: 'Trùng số hoá đơn',
  [EINVOICE_REASON_CODE.INVALID_TAXCODE]: 'Mã số thuế không hợp lệ',
  [EINVOICE_REASON_CODE.INVALID_NEWLINE_CHAR]: 'Dữ liệu chứa ký tự xuống dòng',
  [EINVOICE_REASON_CODE.INVOICE_DATE_RULE]: 'Ngày hoá đơn không hợp lệ',
  [EINVOICE_REASON_CODE.SIGNATURE_INVALID]: 'Chữ ký số không hợp lệ',
  [EINVOICE_REASON_CODE.TOO_MANY_LINES]: 'Vượt quá số dòng cho phép',
  [EINVOICE_REASON_CODE.TAX_REJECTED]: 'CQT từ chối',
  [EINVOICE_REASON_CODE.AMOUNT_WORDS_MISMATCH]: 'Số tiền bằng chữ không khớp',
  [EINVOICE_REASON_CODE.TOKEN_EXPIRED]: 'Token FAST hết hạn',
  [EINVOICE_REASON_CODE.RATE_LIMIT]: 'Vượt giới hạn tốc độ gọi API',
  [EINVOICE_REASON_CODE.UNKNOWN]: 'Lỗi không xác định',
});

// FAST error code → internal reason code mapping
export const FAST_ERROR_TO_REASON = Object.freeze({
  // POS errors
  7801: EINVOICE_REASON_CODE.AUTH_INVALID,
  7802: EINVOICE_REASON_CODE.AUTH_INVALID,
  460: EINVOICE_REASON_CODE.DUPLICATE_KEY,
  78013: EINVOICE_REASON_CODE.INVALID_TAXCODE,
  78306: EINVOICE_REASON_CODE.INVALID_TAXCODE,
  825: EINVOICE_REASON_CODE.INVALID_NEWLINE_CHAR,
  78309: EINVOICE_REASON_CODE.INVOICE_DATE_RULE,
  705: EINVOICE_REASON_CODE.TOO_MANY_LINES,

  // Portal errors
  802: EINVOICE_REASON_CODE.AUTH_INVALID,
  814: EINVOICE_REASON_CODE.AUTH_INVALID,
  809: EINVOICE_REASON_CODE.DUPLICATE_KEY,
  835: EINVOICE_REASON_CODE.DUPLICATE_KEY,
  1002: EINVOICE_REASON_CODE.DUPLICATE_INVOICE_NO,
  730: EINVOICE_REASON_CODE.INVOICE_DATE_RULE,
  731: EINVOICE_REASON_CODE.INVOICE_DATE_RULE,
  63505: EINVOICE_REASON_CODE.SIGNATURE_INVALID,
  3000: EINVOICE_REASON_CODE.TOO_MANY_LINES,
  152: EINVOICE_REASON_CODE.AMOUNT_WORDS_MISMATCH,
});

// ─── Document Types (for PXK support) ─────────────────────
export const EINVOICE_DOC_TYPE = Object.freeze({
  INVOICE: 1, // Hoá đơn GTGT
  PXK: 2, // Phiếu xuất kho
});

// ─── Allowed transitions (state machine guards) ────────────
export const EINVOICE_TRANSITIONS = Object.freeze({
  [EINVOICE_STATUS.DRAFT]: [EINVOICE_STATUS.READY_TO_ISSUE],
  [EINVOICE_STATUS.READY_TO_ISSUE]: [EINVOICE_STATUS.ISSUING],
  [EINVOICE_STATUS.ISSUING]: [
    EINVOICE_STATUS.ISSUED,
    EINVOICE_STATUS.ISSUE_FAILED,
  ],
  [EINVOICE_STATUS.ISSUED]: [EINVOICE_STATUS.TAX_PENDING],
  [EINVOICE_STATUS.TAX_PENDING]: [
    EINVOICE_STATUS.TAX_ACCEPTED,
    EINVOICE_STATUS.TAX_REJECTED,
  ],
  [EINVOICE_STATUS.TAX_ACCEPTED]: [
    EINVOICE_STATUS.CANCELED,
    EINVOICE_STATUS.ADJUSTED,
    EINVOICE_STATUS.REPLACED,
  ],
  [EINVOICE_STATUS.TAX_REJECTED]: [
    EINVOICE_STATUS.READY_TO_ISSUE, // retry with fixed data
  ],
  [EINVOICE_STATUS.ISSUE_FAILED]: [
    EINVOICE_STATUS.READY_TO_ISSUE, // retry
  ],
  // Terminal states: CANCELED, ADJUSTED, REPLACED have no transitions
  [EINVOICE_STATUS.CANCELED]: [],
  [EINVOICE_STATUS.ADJUSTED]: [],
  [EINVOICE_STATUS.REPLACED]: [],
});

// ─── Draft reason codes ────────────────────────────────────────────────────
export const DRAFT_REASON_CODE = Object.freeze({
  // Validation
  VALIDATION_THIEU_MST:               'VALIDATION_THIEU_MST',
  VALIDATION_THIEU_TEN_NGUOI_MUA:     'VALIDATION_THIEU_TEN_NGUOI_MUA',
  VALIDATION_THIEU_EMAIL:             'VALIDATION_THIEU_EMAIL',
  VALIDATION_THIEU_DIA_CHI:           'VALIDATION_THIEU_DIA_CHI',
  VALIDATION_DONG_HANG_TRONG:         'VALIDATION_DONG_HANG_TRONG',
  VALIDATION_TEN_HANG_TRONG:          'VALIDATION_TEN_HANG_TRONG',
  VALIDATION_DON_GIA_SAI:             'VALIDATION_DON_GIA_SAI',
  VALIDATION_SO_LUONG_SAI:            'VALIDATION_SO_LUONG_SAI',
  VALIDATION_THUE_SUAT_KHONG_HOP_LE:  'VALIDATION_THUE_SUAT_KHONG_HOP_LE',
  VALIDATION_MST_KHONG_HOP_LE:       'VALIDATION_MST_KHONG_HOP_LE',
  VALIDATION_EMAIL_KHONG_HOP_LE:     'VALIDATION_EMAIL_KHONG_HOP_LE',
  // Config
  CONFIG_THIEU_VOUCHERBOOK:           'CONFIG_THIEU_VOUCHERBOOK',
  CONFIG_THIEU_BRANCH_MAPPING:        'CONFIG_THIEU_BRANCH_MAPPING',
  CONFIG_THIEU_UNIT_CODE:             'CONFIG_THIEU_UNIT_CODE',
  CONFIG_CHUA_KICH_HOAT:              'CONFIG_CHUA_KICH_HOAT',
  // Eligibility
  ELIGIBILITY_DRAFT_DA_TON_TAI:       'ELIGIBILITY_DRAFT_DA_TON_TAI',
  ELIGIBILITY_HOA_DON_DA_HUY:         'ELIGIBILITY_HOA_DON_DA_HUY',
  ELIGIBILITY_SAI_TRANG_THAI:         'ELIGIBILITY_SAI_TRANG_THAI',
  ELIGIBILITY_KHONG_QUYEN:            'ELIGIBILITY_KHONG_QUYEN',
  ELIGIBILITY_HOA_DON_KHONG_TON:      'ELIGIBILITY_HOA_DON_KHONG_TON',
  // System
  SYSTEM_ERROR:                       'SYSTEM_ERROR',
  SYSTEM_TIMEOUT:                     'SYSTEM_TIMEOUT',
  JOB_FAILED:                         'JOB_FAILED',
});

export const DRAFT_REASON_CODE_META = Object.freeze({
  VALIDATION_THIEU_MST: {
    label: 'Thiếu mã số thuế', description: 'Hóa đơn bán buôn bắt buộc có MST người mua',
    howToFix: 'Vào hóa đơn gốc → cập nhật MST người mua rồi tạo lại',
    blocksCreateDraft: false, blocksIssue: true, severity: 'error', category: 'validation',
  },
  VALIDATION_THIEU_TEN_NGUOI_MUA: {
    label: 'Thiếu tên người mua', description: 'Tên người mua / đơn vị không được để trống',
    howToFix: 'Cập nhật tên khách hàng trên hóa đơn gốc',
    blocksCreateDraft: false, blocksIssue: true, severity: 'error', category: 'validation',
  },
  // blocksIssue: false — email thiếu KHÔNG chặn phát hành; nháp vẫn tạo được và
  // nhân viên có thể bổ sung email sau. Khác MST/địa chỉ là bắt buộc theo luật.
  VALIDATION_THIEU_EMAIL: {
    label: 'Thiếu email', description: 'Policy yêu cầu email để gửi HĐĐT',
    howToFix: 'Bổ sung email trên hóa đơn gốc hoặc trong nháp sau khi tạo',
    blocksCreateDraft: false, blocksIssue: false, severity: 'warning', category: 'validation',
  },
  VALIDATION_THIEU_DIA_CHI: {
    label: 'Thiếu địa chỉ', description: 'Địa chỉ người mua bắt buộc khi có MST',
    howToFix: 'Bổ sung địa chỉ trên hóa đơn gốc',
    blocksCreateDraft: false, blocksIssue: true, severity: 'error', category: 'validation',
  },
  VALIDATION_DONG_HANG_TRONG: {
    label: 'Không có dòng hàng', description: 'Hóa đơn phải có ít nhất 1 dòng hàng hợp lệ',
    howToFix: 'Kiểm tra hóa đơn gốc có sản phẩm/dịch vụ',
    blocksCreateDraft: true, blocksIssue: true, severity: 'error', category: 'validation',
  },
  VALIDATION_TEN_HANG_TRONG: {
    label: 'Tên hàng hóa trống', description: 'Tên sản phẩm/dịch vụ không được rỗng hoặc có ký tự lạ',
    howToFix: 'Sửa tên sản phẩm (loại bỏ ký tự xuống dòng, ký tự lạ)',
    blocksCreateDraft: false, blocksIssue: true, severity: 'error', category: 'validation',
  },
  VALIDATION_DON_GIA_SAI: {
    label: 'Đơn giá không hợp lệ', description: 'Đơn giá phải ≥ 0',
    howToFix: 'Kiểm tra và sửa đơn giá trên hóa đơn gốc',
    blocksCreateDraft: false, blocksIssue: true, severity: 'error', category: 'validation',
  },
  VALIDATION_SO_LUONG_SAI: {
    label: 'Số lượng không hợp lệ', description: 'Số lượng phải > 0',
    howToFix: 'Kiểm tra và sửa số lượng trên hóa đơn gốc',
    blocksCreateDraft: false, blocksIssue: true, severity: 'error', category: 'validation',
  },
  VALIDATION_THUE_SUAT_KHONG_HOP_LE: {
    label: 'Thuế suất không hợp lệ',
    description: 'Thuế suất phải là: 0/5/8/10% hoặc KCT(-1)/KKK(-2)/XK(-9)',
    howToFix: 'Cập nhật thuế suất đúng trên dòng hàng',
    blocksCreateDraft: false, blocksIssue: true, severity: 'error', category: 'validation',
  },
  VALIDATION_MST_KHONG_HOP_LE: {
    label: 'MST sai định dạng',
    description: 'Mã số thuế phải gồm 10 chữ số (hoặc 10-3 cho chi nhánh)',
    howToFix: 'Kiểm tra lại mã số thuế, đảm bảo đúng format 10 chữ số hoặc 10-3',
    blocksCreateDraft: false, blocksIssue: true, severity: 'error', category: 'validation',
  },
  VALIDATION_EMAIL_KHONG_HOP_LE: {
    label: 'Email sai định dạng',
    description: 'Địa chỉ email không đúng format (thiếu @, domain...)',
    howToFix: 'Kiểm tra lại email, đảm bảo có dạng name@domain.com',
    blocksCreateDraft: false, blocksIssue: false, severity: 'warning', category: 'validation',
  },
  CONFIG_THIEU_VOUCHERBOOK: {
    label: 'Chưa cấu hình quyển hóa đơn',
    description: 'Chi nhánh phát hành chưa được liên kết với quyển HĐĐT',
    howToFix: 'Vào Cài đặt → HĐĐT → Cấu hình → thêm chi nhánh + quyển tương ứng',
    blocksCreateDraft: true, blocksIssue: true, severity: 'error', category: 'config',
  },
  CONFIG_THIEU_BRANCH_MAPPING: {
    label: 'Chưa cấu hình chi nhánh',
    description: 'Chi nhánh của hóa đơn chưa có trong bảng cấu hình phát hành',
    howToFix: 'Vào Cài đặt → HĐĐT → Cấu hình → thêm chi nhánh',
    blocksCreateDraft: true, blocksIssue: true, severity: 'error', category: 'config',
  },
  CONFIG_THIEU_UNIT_CODE: {
    label: 'Chưa có mã đơn vị phát hành',
    description: 'unitCode chưa được cấu hình cho chi nhánh này',
    howToFix: 'Liên hệ admin để cấu hình unitCode theo chi nhánh',
    blocksCreateDraft: true, blocksIssue: true, severity: 'error', category: 'config',
  },
  CONFIG_CHUA_KICH_HOAT: {
    label: 'Chức năng HĐĐT chưa kích hoạt',
    description: 'Tích hợp FAST Business Online chưa được bật',
    howToFix: 'Vào Cài đặt → HĐĐT → kích hoạt tích hợp',
    blocksCreateDraft: true, blocksIssue: true, severity: 'error', category: 'config',
  },
  ELIGIBILITY_DRAFT_DA_TON_TAI: {
    label: 'Nháp đã tồn tại', description: 'Hóa đơn này đã có bản nháp HĐĐT',
    howToFix: 'Mở nháp hiện có thay vì tạo mới',
    blocksCreateDraft: false, blocksIssue: false, severity: 'warning',
    category: 'eligibility', isDuplicate: true,
  },
  ELIGIBILITY_HOA_DON_DA_HUY: {
    label: 'Hóa đơn đã bị hủy', description: 'Không thể tạo nháp từ hóa đơn đã hủy',
    howToFix: 'Kiểm tra lại hóa đơn gốc',
    blocksCreateDraft: true, blocksIssue: true, severity: 'error', category: 'eligibility',
  },
  ELIGIBILITY_SAI_TRANG_THAI: {
    label: 'Trạng thái hóa đơn không hợp lệ',
    description: 'Chỉ tạo nháp từ hóa đơn đã hoàn thành',
    howToFix: 'Đảm bảo hóa đơn gốc ở trạng thái đủ điều kiện',
    blocksCreateDraft: true, blocksIssue: true, severity: 'error', category: 'eligibility',
  },
  ELIGIBILITY_KHONG_QUYEN: {
    label: 'Không có quyền',
    description: 'Tài khoản chưa được phân quyền tạo nháp HĐĐT',
    howToFix: 'Liên hệ admin để được cấp quyền EINVOICE_RETAIL_ISSUE hoặc EINVOICE_WHOLESALE_ISSUE',
    blocksCreateDraft: true, blocksIssue: true, severity: 'error', category: 'eligibility',
  },
  ELIGIBILITY_HOA_DON_KHONG_TON: {
    label: 'Không tìm thấy hóa đơn', description: 'Hóa đơn gốc không tồn tại trong hệ thống',
    howToFix: 'Tải lại trang và thử lại',
    blocksCreateDraft: true, blocksIssue: true, severity: 'error', category: 'eligibility',
  },
  SYSTEM_ERROR: {
    label: 'Lỗi hệ thống', description: 'Lỗi không xác định từ server',
    howToFix: 'Thử lại sau vài giây. Nếu lỗi tiếp tục, liên hệ hỗ trợ kèm correlationId',
    blocksCreateDraft: true, blocksIssue: true, severity: 'error', category: 'system',
  },
  SYSTEM_TIMEOUT: {
    label: 'Hết thời gian xử lý', description: 'Server không phản hồi trong thời gian cho phép',
    howToFix: 'Thử lại sau. Kiểm tra kết nối mạng',
    blocksCreateDraft: true, blocksIssue: true, severity: 'error', category: 'system',
  },
  JOB_FAILED: {
    label: 'Job xử lý hàng loạt thất bại', description: 'Tiến trình xử lý nền bị lỗi',
    howToFix: 'Thử tạo lại. Liên hệ hỗ trợ kèm correlationId nếu lỗi lặp lại',
    blocksCreateDraft: true, blocksIssue: true, severity: 'error', category: 'system',
  },
});

/**
 * Trả meta cho một reason code, fallback an toàn nếu không có.
 */
export function getDraftReasonMeta(reasonCode) {
  return DRAFT_REASON_CODE_META[reasonCode] || {
    label:             reasonCode || 'Lỗi không xác định',
    description:       '',
    howToFix:          'Liên hệ hỗ trợ kỹ thuật',
    blocksCreateDraft: true,
    blocksIssue:       true,
    severity:          'error',
    category:          'system',
  };
}

// ─── Buyer Types (per Nghị định 123/2020 & 70/2025) ───────
export const EINVOICE_BUYER_TYPE = Object.freeze({
  INDIVIDUAL: 'INDIVIDUAL',   // Cá nhân Việt Nam
  ENTERPRISE: 'ENTERPRISE',   // Doanh nghiệp/Tổ chức
});

export const EINVOICE_BUYER_TYPE_LABEL = Object.freeze({
  [EINVOICE_BUYER_TYPE.INDIVIDUAL]: 'Cá nhân',
  [EINVOICE_BUYER_TYPE.ENTERPRISE]: 'Doanh nghiệp/Tổ chức',
});

// Bootstrap variant for buyer type badges
export const EINVOICE_BUYER_TYPE_VARIANT = Object.freeze({
  [EINVOICE_BUYER_TYPE.INDIVIDUAL]: 'info',
  [EINVOICE_BUYER_TYPE.ENTERPRISE]: 'primary',
});

// Icon mapping for buyer types (FontAwesome)
export const EINVOICE_BUYER_TYPE_ICON = Object.freeze({
  [EINVOICE_BUYER_TYPE.INDIVIDUAL]: 'fas fa-user',
  [EINVOICE_BUYER_TYPE.ENTERPRISE]: 'fas fa-building',
});

// ─── Buyer Validation Patterns ────────────────────────────
export const TAX_CODE_REGEX = /^(?:\d{10}|\d{12}|\d{13}|\d{10}-\d{3})$/
export const TAX_CODE_FORMAT_ERROR_MESSAGE =
  'MST không hợp lệ (chấp nhận: 10 số, 12 số CCCD, 13 số, hoặc 10-3 số chi nhánh)'

export const BUYER_VALIDATION = Object.freeze({
  TAX_CODE: TAX_CODE_REGEX,                // 10, 12, 13, hoặc 10-3 digits
  CCCD: /^\d{12}$/,                        // CCCD mới 12 số
  CMND: /^\d{9}$/,                         // CMND cũ 9 số
  ID_NUMBER: /^(\d{9}|\d{12})$/,          // CCCD hoặc CMND
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE_VN: /^(0|\+84)\d{9,10}$/,
});

// ─── Multi-email (nhiều email nhận hóa đơn, cách nhau dấu phẩy) ────────────
// Tách danh sách email thô thành mảng đã trim, bỏ phần rỗng
const splitEmailList = (raw) =>
  String(raw || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

// Hợp lệ khi có ít nhất 1 email và mọi email đều đúng định dạng.
// Chuỗi rỗng -> false ở đây; việc "bắt buộc hay không" do tầng required xử lý riêng.
export const isValidEmailList = (raw) => {
  const emails = splitEmailList(raw);
  return emails.length > 0 && emails.every((email) => BUYER_VALIDATION.EMAIL.test(email));
};

// Chuẩn hóa chuỗi email gửi xuống backend: trim từng email, bỏ rỗng, khử trùng lặp,
// nối lại bằng ', ' để dữ liệu luôn nhất quán dù người dùng nhập lộn xộn dấu phẩy/khoảng trắng.
export const normalizeEmailList = (raw) =>
  [...new Set(splitEmailList(raw))].join(', ');

// ─── Buyer Field Requirements per Type ────────────────────
export const BUYER_FIELD_REQUIREMENTS = Object.freeze({
  [EINVOICE_BUYER_TYPE.ENTERPRISE]: {
    required: ['customerName', 'customerTaxCode', 'customerAddress', 'customerEmail'],
    optional: ['buyerName', 'customerPhone'],
  },
  [EINVOICE_BUYER_TYPE.INDIVIDUAL]: {
    required: ['customerName'],
    optional: ['customerIdNumber', 'customerTaxCode', 'customerAddress', 'customerEmail', 'customerPhone'],
  },
});

// ─── Buyer Field Labels (Vietnamese) ──────────────────────
export const BUYER_FIELD_LABELS = Object.freeze({
  customerName: 'Tên người mua',
  customerTaxCode: 'Mã số thuế',
  customerAddress: 'Địa chỉ',
  customerEmail: 'Email',
  customerPhone: 'Số điện thoại',
  buyerName: 'Người đại diện',
  customerIdNumber: 'Số CCCD/CMND',
});

// ─── Buyer Validation Error Messages ──────────────────────
export const BUYER_VALIDATION_MESSAGES = Object.freeze({
  required: {
    customerName: 'Vui lòng nhập tên người mua',
    customerTaxCode: 'Vui lòng nhập mã số thuế',
    customerAddress: 'Vui lòng nhập địa chỉ',
    customerEmail: 'Vui lòng nhập email',
  },
  format: {
    customerTaxCode: TAX_CODE_FORMAT_ERROR_MESSAGE,
    customerIdNumber: 'Số CCCD/CMND không hợp lệ (9 hoặc 12 chữ số)',
    customerEmail: 'Email không đúng định dạng',
    customerPhone: 'Số điện thoại không hợp lệ',
  },
});

// ─── Permissions ───────────────────────────────────────────
export const EINVOICE_PERMISSION = Object.freeze({
  // Retail
  RETAIL_VIEW: 'EINVOICE_VIEW',
  RETAIL_ISSUE: 'EINVOICE_RETAIL_ISSUE',
  RETAIL_CANCEL: 'EINVOICE_RETAIL_CANCEL',
  RETAIL_ADJUST: 'EINVOICE_RETAIL_ADJUST',
  RETAIL_REPLACE: 'EINVOICE_RETAIL_REPLACE',
  RETAIL_PDF: 'EINVOICE_RETAIL_PDF',
  RETAIL_EDIT: 'EINVOICE_RETAIL_EDIT',

  // Wholesale
  WHOLESALE_VIEW: 'EINVOICE_VIEW',
  WHOLESALE_ISSUE: 'EINVOICE_WHOLESALE_ISSUE',
  WHOLESALE_CANCEL: 'EINVOICE_WHOLESALE_CANCEL',
  WHOLESALE_ADJUST: 'EINVOICE_WHOLESALE_ADJUST',
  WHOLESALE_REPLACE: 'EINVOICE_WHOLESALE_REPLACE',
  WHOLESALE_PDF: 'EINVOICE_WHOLESALE_PDF',
  WHOLESALE_EDIT: 'EINVOICE_WHOLESALE_EDIT',
  WHOLESALE_EMAIL: 'EINVOICE_WHOLESALE_EMAIL',

  // Admin
  CONFIG_VIEW: 'EINVOICE_CONFIG_VIEW',
  CONFIG_EDIT: 'EINVOICE_CONFIG_EDIT',
});
